// ============================================================================
// Drawer — a focused, accessible slide-over panel for the program editor and
// exercise picker. Mobile-first: full-width bottom sheet on phones, a
// right-hand panel from sm:. Closes on Escape / backdrop click / the close
// button; locks body scroll; restores focus on close; reduced-motion aware.
// Built in-house so the Programs page stays inside its own folder (no shared-
// component edits) while matching the Pulse surface language.
// ============================================================================
import { useEffect, useId, useRef } from "react";
import type { ReactNode } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/cn";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Sticky footer slot (actions). */
  footer?: ReactNode;
  /** Wider panel for the two-column picker on desktop. */
  wide?: boolean;
}

export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  wide = false,
}: DrawerProps) {
  const { t } = useTranslation("templates");
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const prevActive = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Move focus into the panel.
    const id = window.setTimeout(() => {
      panelRef.current
        ?.querySelector<HTMLElement>(
          "input, select, textarea, button, [tabindex]:not([tabindex='-1'])",
        )
        ?.focus();
    }, 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      window.clearTimeout(id);
      prevActive?.focus?.();
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            aria-hidden
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40, x: 0 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
            className={cn(
              "absolute inset-x-0 bottom-0 flex max-h-[92vh] flex-col rounded-t-xl bg-paper shadow-lg",
              "sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:rounded-l-xl sm:rounded-tr-none",
              wide ? "sm:w-[min(46rem,100vw)]" : "sm:w-[min(34rem,100vw)]",
            )}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-line px-6 pb-4 pt-5">
              <div className="min-w-0">
                <h2
                  id={titleId}
                  className="font-display text-xl font-extrabold tracking-tightest text-body"
                >
                  {title}
                </h2>
                {subtitle && (
                  <p className="mt-0.5 text-sm text-body-soft">{subtitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("actions.close")}
                className="-mr-1 grid h-9 w-9 shrink-0 place-items-center rounded-pill text-body-muted transition-colors hover:bg-surface-sunken hover:text-body"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>

            {/* Footer */}
            {footer && (
              <div className="border-t border-line bg-surface/80 px-6 py-4 backdrop-blur">
                {footer}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
